import { LitElement, html, css, nothing } from "lit";
import { customElement, state } from "lit/decorators.js";

export type BnToastVariant = "info" | "success" | "warning" | "error";

/** Event name the toaster listens for; dispatched by `toast()` / `toastPersistent()`. */
const TOAST_EVENT = "bn-toast";

interface ToastDetail {
  message: string;
  title?: string;
  variant: BnToastVariant;
  /** Auto-dismiss delay in ms. Ignored when `persistent`. */
  durationMs: number;
  persistent: boolean;
}

interface ToastItem extends ToastDetail {
  id: number;
}

function dispatchToast(detail: ToastDetail): void {
  window.dispatchEvent(new CustomEvent<ToastDetail>(TOAST_EVENT, { detail }));
}

/**
 * toast shows a transient notification. Mount a single <bn-toaster> in the app
 * shell; calling toast() from anywhere dispatches a window event it picks up.
 *
 *   toast("Draft saved", "success");
 *   toast(humanError(err), "error");
 */
export function toast(
  message: string,
  variant: BnToastVariant = "info",
  durationMs = 4000,
): void {
  dispatchToast({ message, variant, durationMs, persistent: false });
}

/**
 * toastPersistent shows a notification that stays until the reader dismisses
 * it. This is the variant for asynchronous job outcomes (`SC-F332`): a publish
 * or a proxied CLI run finishes long after the user moved on, and an outcome
 * that fades after four seconds is an outcome nobody read. Give it a `title`
 * naming the job so the notification is legible out of context.
 *
 *   toastPersistent("Generation 12 is live on acme-prod.", "success", "Publish finished");
 *   toastPersistent(err.message, "error", "Publish failed");
 */
export function toastPersistent(
  message: string,
  variant: BnToastVariant = "info",
  title?: string,
): void {
  dispatchToast({ message, title, variant, durationMs: 0, persistent: true });
}

/**
 * bn-toaster is the fixed, bottom-right stack that renders toasts dispatched by
 * `toast()` and `toastPersistent()`. Place exactly one instance in the app
 * shell. Transient toasts auto-dismiss after their duration; persistent ones
 * stay. Every toast carries a real dismiss button, so dismissal is keyboard
 * operable and not a click target hidden on a div.
 *
 * Errors are announced assertively (`role="alert"`); everything else politely
 * (`role="status"`).
 *
 *   <bn-toaster></bn-toaster>
 */
@customElement("bn-toaster")
export class BnToaster extends LitElement {
  @state() private items: ToastItem[] = [];
  private seq = 0;
  private timers = new Map<number, number>();

  private onToast = (e: Event) => {
    const detail = (e as CustomEvent<ToastDetail>).detail;
    const id = ++this.seq;
    this.items = [...this.items, { ...detail, id }];
    if (!detail.persistent && detail.durationMs > 0) {
      this.timers.set(
        id,
        window.setTimeout(() => this.dismiss(id), detail.durationMs),
      );
    }
  };

  override connectedCallback() {
    super.connectedCallback();
    window.addEventListener(TOAST_EVENT, this.onToast);
  }

  override disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener(TOAST_EVENT, this.onToast);
    for (const handle of this.timers.values()) window.clearTimeout(handle);
    this.timers.clear();
  }

  private dismiss(id: number) {
    const handle = this.timers.get(id);
    if (handle !== undefined) {
      window.clearTimeout(handle);
      this.timers.delete(id);
    }
    this.items = this.items.filter((t) => t.id !== id);
  }

  static override styles = css`
    :host {
      position: fixed;
      bottom: var(--bino-space-lg);
      right: var(--bino-space-lg);
      z-index: var(--bino-z-modal);
      display: flex;
      flex-direction: column;
      gap: var(--bino-space-sm);
      pointer-events: none;
    }
    .toast {
      pointer-events: auto;
      min-width: 16rem;
      max-width: 26rem;
      display: flex;
      align-items: flex-start;
      gap: var(--bino-space-sm);
      padding: var(--bino-space-sm) calc(var(--bino-space-sm) * 1.75);
      border-radius: var(--bino-radius);
      background: var(--bino-surface);
      border: 1px solid var(--bino-border);
      border-left-width: 3px;
      box-shadow: var(--bino-shadow-dropdown);
      font-family: var(--bino-font-sans);
      font-size: var(--bino-font-size-base);
      color: var(--bino-text);
      animation: slide-in var(--bino-transition-normal);
    }
    .toast.info {
      border-left-color: var(--bino-info);
    }
    .toast.success {
      border-left-color: var(--bino-success);
    }
    .toast.warning {
      border-left-color: var(--bino-warning);
    }
    .toast.error {
      border-left-color: var(--bino-error);
    }
    .body {
      flex: 1;
      min-width: 0;
    }
    .title {
      font-weight: 600;
      margin-bottom: var(--bino-space-xs);
    }
    .msg {
      word-break: break-word;
      color: var(--bino-text-secondary);
    }
    .toast:not(.titled) .msg {
      color: var(--bino-text);
    }
    .dismiss {
      flex-shrink: 0;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 1.25rem;
      height: 1.25rem;
      padding: 0;
      background: none;
      border: 1px solid transparent;
      border-radius: var(--bino-radius-sm);
      font-family: var(--bino-font-sans);
      font-size: var(--bino-font-size-md);
      line-height: 1;
      color: var(--bino-text-secondary);
      cursor: pointer;
      transition: color var(--bino-transition-fast), background var(--bino-transition-fast);
    }
    .dismiss:hover {
      background: var(--bino-surface-hover);
      color: var(--bino-text);
    }
    .dismiss:focus-visible {
      outline: none;
      border-color: var(--bino-primary);
      box-shadow: 0 0 0 3px var(--bino-focus-ring);
    }
    @keyframes slide-in {
      from {
        opacity: 0;
        transform: translateY(8px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }
    @media (prefers-reduced-motion: reduce) {
      .toast {
        animation: none;
      }
    }
  `;

  override render() {
    return html`${this.items.map(
      (t) => html`<div
        class="toast ${t.variant} ${t.title ? "titled" : ""}"
        role=${t.variant === "error" ? "alert" : "status"}
        aria-live=${t.variant === "error" ? "assertive" : "polite"}
      >
        <div class="body">
          ${t.title ? html`<div class="title">${t.title}</div>` : nothing}
          <div class="msg">${t.message}</div>
        </div>
        <button
          class="dismiss"
          type="button"
          aria-label="Dismiss notification"
          @click=${() => this.dismiss(t.id)}
        >
          &times;
        </button>
      </div>`,
    )}`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "bn-toaster": BnToaster;
  }
}
